
/**
 * Enhanced storage service designed to handle large datasets (5000+ students)
 * Uses data compression and intelligent storage management
 */

interface StorageStats {
  totalStudents: number;
  storageUsed: string;
  compressionRatio: number;
  lastBackup: string;
}

class LargeDataStorage {
  private readonly STORAGE_KEY = 'nalc_students';
  private readonly BACKUP_KEY = 'nalc_students_backup';
  private readonly CHUNK_SIZE = 100; // Students per chunk
  private cache = new Map<string, any>();

  async getStudents(): Promise<any[]> {
    try {
      // Try cache first
      if (this.cache.has(this.STORAGE_KEY)) {
        const cached = this.cache.get(this.STORAGE_KEY);
        console.log(`📦 Retrieved ${cached.length} students from cache`);
        return cached;
      }

      // Try main storage
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const students = this.decompressData(data);
        this.cache.set(this.STORAGE_KEY, students);
        console.log(`📦 Retrieved ${students.length} students from main storage`);
        return students;
      }

      // Try backup if main fails
      const backupData = localStorage.getItem(this.BACKUP_KEY);
      if (backupData) {
        const students = this.decompressData(backupData);
        this.cache.set(this.STORAGE_KEY, students);
        console.log(`📦 Retrieved ${students.length} students from backup`);
        // Don't restore main storage here to avoid quota issues
        return students;
      }

      console.log('📦 No students found in storage, returning empty array');
      return [];
    } catch (error) {
      console.error('Error loading students:', error);
      return this.getFromBackup();
    }
  }

  async saveStudents(students: any[]): Promise<void> {
    try {
      // Update cache immediately for instant access
      this.cache.set(this.STORAGE_KEY, students);
      console.log(`📦 Updated cache with ${students.length} students`);

      // Clean up old data first to make space
      this.clearOldData();

      // Use ultra compression for large datasets
      const compressedData = this.ultraCompressData(students);
      
      // Try to save to main storage
      try {
        localStorage.setItem(this.STORAGE_KEY, compressedData);
        console.log(`✅ Saved ${students.length} students to main storage (compressed)`);
      } catch (quotaError) {
        console.warn('📦 Main storage quota exceeded, using backup only');
        // Don't throw error, just use cache and backup
      }
      
      // Always try to save backup asynchronously
      setTimeout(() => {
        try {
          localStorage.setItem(this.BACKUP_KEY, compressedData);
          console.log(`✅ Backup saved with ${students.length} students`);
        } catch (e) {
          console.warn('📦 Backup save failed, but cache is available');
        }
      }, 50);

    } catch (error) {
      console.error('Error in saveStudents:', error);
      // Don't throw error - cache is still updated
    }
  }

  private clearOldData(): void {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('nalc_backup_') || 
        key.startsWith('nalc_temp_') ||
        key.startsWith('nalc_emergency_backup_') ||
        key.startsWith('_old_')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore removal errors
      }
    });
    console.log(`🧹 Cleaned up ${keysToRemove.length} old storage keys`);
  }

  private ultraCompressData(data: any[]): string {
    // Ultra compression: minimal fields only with shorter keys
    const minimal = data.map(s => ({
      i: s.id, // id
      n: s.name, // name  
      s: s.surname || '', // surname
      a: s.age || 0, // age
      c: s.contactNumber || '', // contact
      e: s.email || '', // email
      addr: s.address || '', // address
      p: (s.programs || []).map(p => ({
        t: p.title, // title
        g: p.category, // category
        f: p.fee || 0, // fee
        d: p.duration || 30, // duration
        fs: p.feeStatus || 'pending', // feeStatus
        ea: p.enrolledAt || new Date().toISOString(), // enrolledAt
        cd: p.completionDate // completionDate
      })),
      ca: s.createdAt || new Date().toISOString(), // createdAt
      ua: s.updatedAt || new Date().toISOString() // updatedAt
    }));
    
    return JSON.stringify(minimal);
  }

  private decompressData(compressedData: string): any[] {
    try {
      const data = JSON.parse(compressedData);
      
      // Check if it's ultra-compressed format (has 'i' field instead of 'id')
      if (data.length > 0 && data[0].i !== undefined) {
        return this.expandUltraCompressed(data);
      }
      
      // Regular format, ensure all required fields exist
      return data.map(student => ({
        id: student.id,
        name: student.name || '',
        surname: student.surname || '',
        fathersName: student.fathersName || '',
        age: student.age || 0,
        address: student.address || '',
        province: student.province || '',
        country: student.country || '',
        contactNumber: student.contactNumber || '',
        email: student.email || '',
        profileImage: student.profileImage || '',
        qualificationLevel: student.qualificationLevel || '',
        documents: student.documents || [],
        institutionAddress: student.institutionAddress || '',
        institutionPhone: student.institutionPhone || '',
        institutionEmail: student.institutionEmail || '',
        authorizedBy: student.authorizedBy || '',
        policyNote: student.policyNote || '',
        programs: student.programs || [],
        createdAt: student.createdAt || new Date().toISOString(),
        updatedAt: student.updatedAt || new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error decompressing data:', error);
      return [];
    }
  }

  private expandUltraCompressed(data: any[]): any[] {
    return data.map(s => ({
      id: s.i,
      name: s.n,
      surname: s.s || '',
      fathersName: '',
      age: s.a || 0,
      address: s.addr || '',
      province: '',
      country: '',
      contactNumber: s.c || '',
      email: s.e || '',
      profileImage: '',
      qualificationLevel: '',
      documents: [],
      institutionAddress: '',
      institutionPhone: '',
      institutionEmail: '',
      authorizedBy: '',
      policyNote: '',
      programs: (s.p || []).map(p => ({
        title: p.t,
        category: p.g,
        fee: p.f || 0,
        enrolledAt: p.ea || new Date().toISOString(),
        duration: p.d || 30,
        completionDate: p.cd || new Date(Date.now() + (p.d || 30) * 24 * 60 * 60 * 1000).toISOString(),
        feeStatus: p.fs || 'pending',
        nextPaymentDate: undefined
      })),
      createdAt: s.ca || new Date().toISOString(),
      updatedAt: s.ua || new Date().toISOString()
    }));
  }

  private async getFromBackup(): Promise<any[]> {
    try {
      const backupData = localStorage.getItem(this.BACKUP_KEY);
      if (backupData) {
        return this.decompressData(backupData);
      }
      return [];
    } catch (error) {
      console.error('Backup recovery failed:', error);
      return [];
    }
  }

  async getStorageStats(): Promise<StorageStats> {
    const students = await this.getStudents();
    const rawSize = JSON.stringify(students).length;
    const compressedSize = localStorage.getItem(this.STORAGE_KEY)?.length || 0;
    
    return {
      totalStudents: students.length,
      storageUsed: `${(compressedSize / 1024).toFixed(2)} KB`,
      compressionRatio: rawSize > 0 ? (compressedSize / rawSize) : 1,
      lastBackup: new Date().toLocaleString()
    };
  }

  clearCache(): void {
    this.cache.clear();
    console.log('🧹 Cache cleared');
  }

  // Method to ensure a student exists by ID - for immediate access after creation
  findStudentById(id: number): any | null {
    const cached = this.cache.get(this.STORAGE_KEY);
    if (cached && Array.isArray(cached)) {
      const found = cached.find(s => s.id === id);
      if (found) {
        console.log(`✅ Found student ${id} in cache: ${found.name}`);
        return found;
      }
    }
    console.log(`❌ Student ${id} not found in cache`);
    return null;
  }
}

export const largeDataStorage = new LargeDataStorage();
